import { useState, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import AppModal from './AppModal';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import axios from '../utils/axios';
import { helpDeskTickets, helpDeskTicketAttachments } from '../utils/urls';
import { PRIORITY_OPTIONS, CATEGORY_OPTIONS } from '../pages/helpDesk/helpDeskConstants';

const EMPTY_FORM = { title: '', description: '', priority: 'medium', category: 'bug' };

export default function FloatingTicketButton() {
    const toast = useRef(null);
    const btnRef = useRef(null);
    const uploadRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [screenshot, setScreenshot] = useState(null);
    const [capturing, setCapturing] = useState(false);
    const [aiOpen, setAiOpen] = useState(false);
    const isSuperuser = useSelector((state) => state.user.isSuperuser);

    const captureAndOpen = useCallback(async () => {
        setCapturing(true);
        if (btnRef.current) btnRef.current.style.visibility = 'hidden';
        try {
            // Use native Screen Capture API — pixel-perfect, captures actual scroll position
            const stream = await navigator.mediaDevices.getDisplayMedia({
                video: { displaySurface: 'browser' },
                audio: false,
                preferCurrentTab: true,
                selfBrowserSurface: 'include',
            });
            const track = stream.getVideoTracks()[0];
            const bitmap = await new ImageCapture(track).grabFrame();
            track.stop();
            const canvas = document.createElement('canvas');
            canvas.width = bitmap.width;
            canvas.height = bitmap.height;
            canvas.getContext('2d').drawImage(bitmap, 0, 0);
            setScreenshot(canvas.toDataURL('image/jpeg', 0.92));
        } catch (_) {
            setScreenshot(null);
        } finally {
            if (btnRef.current) btnRef.current.style.visibility = 'visible';
            setCapturing(false);
        }
        setForm(EMPTY_FORM);
        setErrors({});
        setOpen(true);
    }, []);

    const handleUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => setScreenshot(ev.target.result);
        reader.readAsDataURL(file);
        e.target.value = '';
    };

    const setField = (key, val) => {
        setForm(prev => ({ ...prev, [key]: val }));
        if (errors[key]) setErrors(prev => ({ ...prev, [key]: undefined }));
    };

    const handleSubmit = async () => {
        const errs = {};
        if (!form.title.trim()) errs.title = 'Required';
        if (Object.keys(errs).length) { setErrors(errs); return; }

        setSubmitting(true);
        try {
            const res = await axios.post(helpDeskTickets, {
                title: form.title.trim(),
                description: form.description.trim() || undefined,
                priority: form.priority,
                category: form.category,
            });

            if (screenshot && res.data?.id) {
                try {
                    const blob = await fetch(screenshot).then(r => r.blob());
                    const fd = new FormData();
                    fd.append('file', blob, 'screenshot.jpg');
                    await axios.post(helpDeskTicketAttachments(res.data.id), fd);
                } catch (_) {}
            }

            toast.current?.show({ severity: 'success', summary: 'Ticket Created', detail: `#${res.data.id} — ${res.data.title}`, life: 4000 });
            setOpen(false);
        } catch (err) {
            const msg = err?.response?.data?.detail || 'Failed to create ticket';
            toast.current?.show({ severity: 'error', summary: 'Error', detail: msg, life: 4000 });
        } finally {
            setSubmitting(false);
        }
    };

    const fieldStyle = (hasError) => ({
        width: '100%',
        // background: '#1a2b4a',
        border: `1px solid ${hasError ? '#e2574a' : 'rgba(255,255,255,0.12)'}`,
        color: 'black',
        borderRadius: 8,
    });

    return (
        <>
            <Toast ref={toast} position="bottom-right" />

            {/* AI floating button — superuser only */}
            {isSuperuser && (
                <button
                    onClick={() => setAiOpen(true)}
                    title="AI Assistant"
                    style={{
                        position: 'fixed',
                        bottom: 28,
                        right: 90,
                        zIndex: 9998,
                        width: 52,
                        height: 52,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #7c3aed, #5b21b6)',
                        border: 'none',
                        cursor: 'pointer',
                        boxShadow: '0 4px 20px rgba(124,58,237,0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: 22,
                        transition: 'transform 0.15s, box-shadow 0.15s',
                    }}
                    onMouseEnter={e => {
                        e.currentTarget.style.transform = 'scale(1.12)';
                        e.currentTarget.style.boxShadow = '0 6px 28px rgba(124,58,237,0.7)';
                    }}
                    onMouseLeave={e => {
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.boxShadow = '0 4px 20px rgba(124,58,237,0.5)';
                    }}
                >
                    <span style={{ fontWeight: 800, fontSize: 18, letterSpacing: '-0.5px', color: 'white' }}>AI</span>
                </button>
            )}

            {/* AI Assistant modal */}
            <AppModal
                zIndex={9999}
                header={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ background: 'linear-gradient(135deg, #7c3aed, #5b21b6)', borderRadius: 8, width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <span style={{ fontWeight: 800, fontSize: 13, color: 'white' }}>AI</span>
                        </div>
                        <div style={{ fontWeight: 700, fontSize: 15, color: 'white' }}>AI Assistant</div>
                    </div>
                }
                visible={aiOpen}
                panelStyle={{ width: 1200, height: '90vh' }}
                onHide={() => setAiOpen(false)}
                footer={null}
            >
                <iframe
                    src="https://ai.bcrumbs.com"
                    title="AI Assistant"
                    style={{ width: '100%', height: '70vh', border: 'none', borderRadius: 8 }}
                />
            </AppModal>

            {/* Floating button */}
            <button
                ref={btnRef}
                onClick={captureAndOpen}
                disabled={capturing}
                title="Report an issue"
                style={{
                    position: 'fixed',
                    bottom: 28,
                    right: 28,
                    zIndex: 9998,
                    width: 52,
                    height: 52,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #1e6fd9, #1453a8)',
                    border: 'none',
                    cursor: capturing ? 'wait' : 'pointer',
                    boxShadow: '0 4px 20px rgba(30,111,217,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: 22,
                    transition: 'transform 0.15s, box-shadow 0.15s',
                }}
                onMouseEnter={e => {
                    e.currentTarget.style.transform = 'scale(1.12)';
                    e.currentTarget.style.boxShadow = '0 6px 28px rgba(30,111,217,0.7)';
                }}
                onMouseLeave={e => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = '0 4px 20px rgba(30,111,217,0.5)';
                }}
            >
                <i className={capturing ? 'pi pi-spin pi-spinner' : 'pi pi-question-circle'} />
            </button>

            {/* Dialog */}
            <AppModal
                zIndex={9999}
                header={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ background: 'linear-gradient(135deg, #1e6fd9, #1453a8)', borderRadius: 8, width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <i className="pi pi-ticket" style={{ color: 'white', fontSize: 14 }} />
                        </div>
                        <div>
                            <div style={{ fontWeight: 700, fontSize: 15, color: 'white' }}>Report an Issue</div>
                            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', fontWeight: 400 }}>
                                {screenshot ? 'Screenshot attached automatically' : 'Screenshot unavailable'}
                            </div>
                        </div>
                    </div>
                }
                visible={open}
                panelStyle={{ width: 520 }}
                onHide={() => !submitting && setOpen(false)}
                footer={
                    <div style={{ background: 'transparent', borderTop: '1px solid rgba(255,255,255,0.08)', padding: '0.625rem 1.25rem', display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
                        <Button
                            label="Cancel"
                            onClick={() => setOpen(false)}
                            disabled={submitting}
                            style={{ boxShadow: 'none', background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.7)', borderRadius: 6, padding: '5px 14px', fontSize: 13 }}
                        />
                        <Button
                            label={submitting ? 'Submitting…' : 'Submit Ticket'}
                            icon={submitting ? 'pi pi-spin pi-spinner' : 'pi pi-send'}
                            onClick={handleSubmit}
                            disabled={submitting}
                            style={{ boxShadow: 'none', background: 'linear-gradient(135deg, #1e6fd9, #1453a8)', border: 'none', borderRadius: 6, fontWeight: 600, padding: '5px 14px', fontSize: 13 }}
                        />
                    </div>
                }
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {/* Screenshot preview + upload */}
                    <div>
                        <input
                            ref={uploadRef}
                            type="file"
                            accept="image/*"
                            style={{ display: 'none' }}
                            onChange={handleUpload}
                        />
                        {screenshot ? (
                            <div style={{ position: 'relative', borderRadius: 8, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.1)', maxHeight: 150 }}>
                                <img
                                    src={screenshot}
                                    alt="Page screenshot"
                                    style={{ width: '100%', display: 'block', objectFit: 'cover', objectPosition: 'top' }}
                                />
                                <div style={{ position: 'absolute', top: 6, right: 6, display: 'flex', gap: 4 }}>
                                    <button
                                        onClick={() => uploadRef.current?.click()}
                                        title="Replace screenshot"
                                        style={{ background: 'rgba(0,0,0,0.65)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 5, color: 'white', fontSize: 11, padding: '3px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                                    >
                                        <i className="pi pi-upload" style={{ fontSize: 11 }} /> Replace
                                    </button>
                                    <button
                                        onClick={() => setScreenshot(null)}
                                        title="Remove screenshot"
                                        style={{ background: 'rgba(0,0,0,0.65)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 5, color: 'white', fontSize: 11, padding: '3px 8px', cursor: 'pointer' }}
                                    >
                                        <i className="pi pi-times" style={{ fontSize: 11 }} />
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <button
                                onClick={() => uploadRef.current?.click()}
                                style={{ width: '100%', padding: '14px', borderRadius: 8, border: '1.5px dashed rgba(255,255,255,0.18)', background: 'transparent', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontSize: 13 }}
                            >
                                <i className="pi pi-image" /> Upload screenshot
                            </button>
                        )}
                    </div>

                    {/* Title */}
                    <div>
                        <label style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13, display: 'block', marginBottom: 6 }}>
                            Title <span style={{ color: '#e2574a' }}>*</span>
                        </label>
                        <InputText
                            value={form.title}
                            onChange={e => setField('title', e.target.value)}
                            placeholder="Brief description of the issue"
                            style={fieldStyle(errors.title)}
                        />
                        {errors.title && <span style={{ color: '#e2574a', fontSize: 12, marginTop: 4, display: 'block' }}>{errors.title}</span>}
                    </div>

                    {/* Description */}
                    <div>
                        <label style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13, display: 'block', marginBottom: 6 }}>Description</label>
                        <InputTextarea
                            value={form.description}
                            onChange={e => setField('description', e.target.value)}
                            placeholder="Steps to reproduce, expected vs actual behaviour…"
                            rows={3}
                            autoResize
                            style={{ ...fieldStyle(false), resize: 'none' }}
                        />
                    </div>

                    {/* Priority + Category */}
                    <div style={{ display: 'flex', gap: '1rem' }}>
                        <div style={{ flex: 1 }}>
                            <label style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13, display: 'block', marginBottom: 6 }}>Priority</label>
                            <Dropdown
                                value={form.priority}
                                options={PRIORITY_OPTIONS}
                                onChange={e => setField('priority', e.value)}
                                style={{ width: '100%' }}
                            />
                        </div>
                        <div style={{ flex: 1 }}>
                            <label style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13, display: 'block', marginBottom: 6 }}>Category</label>
                            <Dropdown
                                value={form.category}
                                options={CATEGORY_OPTIONS}
                                onChange={e => setField('category', e.value)}
                                style={{ width: '100%' }}
                            />
                        </div>
                    </div>
                </div>
            </AppModal>
        </>
    );
}
